import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const BASE = '/api/discord-mappings';

export default function DiscordMappings() {
  const { getAuthHeader } = useAuth();
  const [mappings, setMappings] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newDiscordId, setNewDiscordId] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('mappings');
  const [filter, setFilter] = useState('');
  // Inline-edit state for assigning an email to an existing row.
  const [editingId, setEditingId] = useState(null);
  const [editingEmail, setEditingEmail] = useState('');
  const [savingId, setSavingId] = useState(null);
  // Ref-based focus: autoFocus on a dynamically-inserted <input> inside a
  // table cell isn't reliable — depending on browser/React timing the
  // focus stays on whatever was focused before (the search box, in our
  // case), which made the user's email keystrokes filter the table away.
  // useEffect keyed on editingId pulls focus deterministically.
  const editInputRef = useRef(null);
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  const hdrs = () => ({
    Authorization: getAuthHeader(),
    'Content-Type': 'application/json',
  });

  // SECURITY_AUDIT M-12: admin-only is enforced server-side (returns 403
  // for non-admin sessions). Client also surfaces a clear "not authorized"
  // message so non-admins who land on /discord-mappings don't see an
  // infinite "Loading..." spinner with no explanation. Server remains the
  // actual gate; this is UX only.
  const [unauthorized, setUnauthorized] = useState(false);

  const load = async () => {
    try {
      const [mRes, tRes] = await Promise.all([
        fetch(BASE, { headers: hdrs(), credentials: 'include' }),
        fetch(`${BASE}/transcripts`, { headers: hdrs(), credentials: 'include' }),
      ]);
      if (mRes.status === 403 || tRes.status === 403) {
        setUnauthorized(true);
        return;
      }
      const mData = await mRes.json();
      const tData = await tRes.json();
      if (mData.ok) setMappings(mData.mappings || []);
      if (tData.ok) setTranscripts(tData.transcripts || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newDiscordId.trim() || !newEmail.trim()) return;
    setError(null);
    try {
      const res = await fetch(BASE, {
        method: 'POST',
        headers: hdrs(),
        credentials: 'include',
        body: JSON.stringify({
          discordId: newDiscordId.trim(),
          email: newEmail.trim(),
          displayName: newDisplayName.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setNewDiscordId(''); setNewEmail(''); setNewDisplayName('');
        load();
      } else setError(data.error);
    } catch (e) { setError(e.message); }
  };

  const handleDelete = async (discordId) => {
    if (!window.confirm(`Remove mapping for ${discordId}?`)) return;
    await fetch(`${BASE}/${discordId}`, { method: 'DELETE', headers: hdrs(), credentials: 'include' });
    load();
  };

  const startEdit = (m) => {
    setEditingId(m.discordId);
    setEditingEmail(m.email || '');
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingEmail('');
  };

  // POST upserts via merge — same endpoint used for Add. Re-using the
  // existing displayName means we update only the email (the bot's
  // /sync-users will re-fill displayName on its own anyway).
  const saveEdit = async (m) => {
    const email = editingEmail.trim();
    if (!email) { setError('Email cannot be empty'); return; }
    setSavingId(m.discordId);
    setError(null);
    try {
      const res = await fetch(BASE, {
        method: 'POST',
        headers: hdrs(),
        credentials: 'include',
        body: JSON.stringify({
          discordId: m.discordId,
          email,
          displayName: m.displayName || null,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        cancelEdit();
        load();
      } else setError(data.error);
    } catch (e) { setError(e.message); }
    finally { setSavingId(null); }
  };

  const viewTranscript = async (id) => {
    try {
      const res = await fetch(`${BASE}/transcripts/${id}`, { headers: hdrs(), credentials: 'include' });
      const data = await res.json();
      if (data.ok) setSelectedTranscript(data.transcript);
    } catch (e) { setError(e.message); }
  };

  const s = {
    // height + overflowY make the whole admin page a scroll surface — was
    // only minHeight: 100vh which means a long mappings table or long
    // transcript would push content past the viewport with nowhere to
    // scroll on mobile (the page lived inside a fixed container).
    page: { maxWidth: 800, margin: '0 auto', padding: 20, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', color: 'var(--text-primary, #0d0d0d)', background: 'var(--bg-primary, #ffffff)', height: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
    tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border-color, #e3e3e8)' },
    tab: { padding: '8px 16px', background: 'none', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', color: 'var(--text-secondary, #676767)' },
    tabActive: { color: 'var(--text-primary, #0d0d0d)', borderBottomColor: '#007bff', fontWeight: 500 },
    table: { width: '100%', borderCollapse: 'collapse', marginBottom: 24 },
    th: { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid var(--border-color, #e3e3e8)', fontSize: 13 },
    td: { padding: '8px 12px', borderBottom: '1px solid var(--border-color, #e3e3e8)', fontSize: 14 },
    form: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
    input: { padding: '6px 10px', border: '1px solid var(--border-color, #e3e3e8)', borderRadius: 6, fontFamily: 'inherit', background: 'var(--bg-secondary, #f7f7f8)', color: 'var(--text-primary, #0d0d0d)' },
    btn: { padding: '6px 14px', borderRadius: 6, border: 'none', fontWeight: 500, cursor: 'pointer' },
    btnP: { background: '#007bff', color: 'white' },
    btnD: { background: '#dc3545', color: 'white', fontSize: 12, padding: '4px 10px' },
    btnS: { background: 'var(--border-color, #e3e3e8)', color: 'var(--text-primary, #0d0d0d)', fontSize: 12, padding: '4px 10px' },
    error: { background: '#ffebee', color: '#c62828', padding: '8px 12px', borderRadius: 8, marginBottom: 16 },
    transcript: { whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5, padding: 16, background: 'var(--bg-secondary, #f7f7f8)', borderRadius: 8, maxHeight: '60vh', overflowY: 'auto' },
    link: { color: '#007bff', textDecoration: 'none', cursor: 'pointer' },
  };

  if (unauthorized) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Discord Admin</h1>
          <a href="/" style={{ color: 'var(--text-secondary, #676767)', textDecoration: 'none' }}>Back to Assistant</a>
        </div>
        <div style={s.error}>
          You don't have access to this page. Contact an admin if you think you should.
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Discord Admin</h1>
        <a href="/" style={{ color: 'var(--text-secondary, #676767)', textDecoration: 'none' }}>Back to Assistant</a>
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.tabs}>
        <button style={{ ...s.tab, ...(tab === 'mappings' ? s.tabActive : {}) }} onClick={() => setTab('mappings')}>
          User Mappings ({mappings.length})
        </button>
        <button style={{ ...s.tab, ...(tab === 'transcripts' ? s.tabActive : {}) }} onClick={() => { setTab('transcripts'); setSelectedTranscript(null); }}>
          Meeting Transcripts ({transcripts.length})
        </button>
      </div>

      {loading ? <p>Loading...</p> : tab === 'mappings' ? (
        <>
          <p style={{ color: 'var(--text-secondary, #676767)', marginBottom: 16, fontSize: 14 }}>
            Map Discord user IDs to email addresses for meeting insights integration. Use <code>/sync-users</code> in Discord to auto-capture IDs.
          </p>
          <form onSubmit={handleAdd} style={s.form}>
            <input style={{ ...s.input, width: 180 }} placeholder="Discord User ID" value={newDiscordId} onChange={e => setNewDiscordId(e.target.value)} />
            <input style={{ ...s.input, width: 220 }} placeholder="Email" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            <input style={{ ...s.input, width: 150 }} placeholder="Display Name" value={newDisplayName} onChange={e => setNewDisplayName(e.target.value)} />
            <button type="submit" style={{ ...s.btn, ...s.btnP }}>Add</button>
          </form>
          <input
            style={{ ...s.input, width: '100%', marginBottom: 12 }}
            placeholder="Search ID, name, or email…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          {(() => {
            // Case-insensitive substring match across the visible columns.
            const needle = filter.trim().toLowerCase();
            const filtered = needle
              ? mappings.filter(m =>
                  [m.discordId, m.displayName, m.email]
                    .filter(Boolean)
                    .some(v => String(v).toLowerCase().includes(needle))
                )
              : mappings;
            if (mappings.length === 0) {
              return <p style={{ color: 'var(--text-secondary, #676767)' }}>No mappings yet.</p>;
            }
            if (filtered.length === 0) {
              return <p style={{ color: 'var(--text-secondary, #676767)' }}>No mappings match your search.</p>;
            }
            return (
              <table style={s.table}>
                <thead><tr><th style={s.th}>Discord ID</th><th style={s.th}>Name</th><th style={s.th}>Email</th><th style={s.th}></th></tr></thead>
                <tbody>
                  {filtered.map(m => {
                    const isEditing = editingId === m.discordId;
                    const isSaving = savingId === m.discordId;
                    return (
                      <tr key={m.discordId}>
                        <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 13 }}>{m.discordId}</td>
                        <td style={s.td}>{m.displayName || '-'}</td>
                        <td style={s.td}>
                          {isEditing ? (
                            <input
                              ref={editInputRef}
                              style={{ ...s.input, width: 240 }}
                              type="email"
                              placeholder="email@distributedglobal.com"
                              value={editingEmail}
                              onChange={e => setEditingEmail(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveEdit(m);
                                else if (e.key === 'Escape') cancelEdit();
                              }}
                            />
                          ) : (
                            m.email || <span style={{ color: '#dc3545' }}>not set</span>
                          )}
                        </td>
                        <td style={{ ...s.td, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {isEditing ? (
                            <>
                              <button onClick={() => saveEdit(m)} style={{ ...s.btn, ...s.btnP, fontSize: 12, padding: '4px 10px' }} disabled={isSaving}>
                                {isSaving ? 'Saving…' : 'Save'}
                              </button>
                              <button onClick={cancelEdit} style={{ ...s.btn, ...s.btnS }} disabled={isSaving}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => startEdit(m)} style={{ ...s.btn, ...s.btnS }}>
                                {m.email ? 'Edit' : 'Assign email'}
                              </button>
                              <button onClick={() => handleDelete(m.discordId)} style={{ ...s.btn, ...s.btnD }}>Remove</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </>
      ) : selectedTranscript ? (
        <>
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setSelectedTranscript(null)} style={{ ...s.btn, ...s.btnS }}>Back</button>
            <strong>{selectedTranscript.channelName}</strong>
            <span style={{ color: 'var(--text-secondary, #676767)' }}>
              {selectedTranscript.guildName} — {new Date(selectedTranscript.startedAt).toLocaleString()}
            </span>
            <span style={{ color: 'var(--text-secondary, #676767)', fontSize: 13 }}>
              {selectedTranscript.speakers?.join(', ')}
            </span>
          </div>
          <div style={s.transcript}>{selectedTranscript.transcript || '(empty)'}</div>
        </>
      ) : (
        <>
          <p style={{ color: 'var(--text-secondary, #676767)', marginBottom: 16, fontSize: 14 }}>
            Transcripts from Discord voice meetings. Click to view full transcript.
          </p>
          <input
            style={{ ...s.input, width: '100%', marginBottom: 12 }}
            placeholder="Search channel, server, or speaker…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          {(() => {
            const needle = filter.trim().toLowerCase();
            const filtered = needle
              ? transcripts.filter(t => {
                  const speakers = Array.isArray(t.speakers) ? t.speakers.join(' ') : '';
                  return [t.channelName, t.guildName, speakers, new Date(t.startedAt).toLocaleString()]
                    .filter(Boolean)
                    .some(v => String(v).toLowerCase().includes(needle));
                })
              : transcripts;
            if (transcripts.length === 0) {
              return <p style={{ color: 'var(--text-secondary, #676767)' }}>No transcripts yet. Join a watched voice channel and have a conversation.</p>;
            }
            if (filtered.length === 0) {
              return <p style={{ color: 'var(--text-secondary, #676767)' }}>No transcripts match your search.</p>;
            }
            return (
              <table style={s.table}>
                <thead><tr><th style={s.th}>Date</th><th style={s.th}>Channel</th><th style={s.th}>Server</th><th style={s.th}>Speakers</th><th style={s.th}>Segments</th><th style={s.th}></th></tr></thead>
                <tbody>
                  {filtered.map(t => (
                    <tr key={t.id}>
                      <td style={s.td}>{new Date(t.startedAt).toLocaleString()}</td>
                      <td style={s.td}>{t.channelName}</td>
                      <td style={s.td}>{t.guildName}</td>
                      <td style={s.td}>{t.speakers?.join(', ') || '-'}</td>
                      <td style={s.td}>{t.segmentCount}</td>
                      <td style={s.td}><span style={s.link} onClick={() => viewTranscript(t.id)}>View</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </>
      )}
    </div>
  );
}
